import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

let scrollLockCount = 0;
let savedBodyOverflow = '';
let savedHtmlOverflow = '';
let savedBodyPaddingRight = '';

function lockPageScroll() {
  scrollLockCount += 1;
  if (scrollLockCount !== 1) return;

  savedBodyOverflow = document.body.style.overflow;
  savedHtmlOverflow = document.documentElement.style.overflow;
  savedBodyPaddingRight = document.body.style.paddingRight;

  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
}

function unlockPageScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount !== 0) return;

  document.body.style.overflow = savedBodyOverflow;
  document.documentElement.style.overflow = savedHtmlOverflow;
  document.body.style.paddingRight = savedBodyPaddingRight;
}

/**
 * Full-viewport modal backdrop (portal + scroll lock).
 * Clicks on the dimmed area outside the dialog call onClose when closeOnBackdrop is true.
 */
export default function ModalOverlay({
  open,
  onClose,
  children,
  zIndexClass = 'z-[100]',
  closeOnBackdrop = true,
  align = 'center',
  className = '',
  /** When true, ESC is handled by globalModalManager (StandardModal / PreviewModal) only */
  suppressEscapeKey = false,
}) {
  useEffect(() => {
    if (!open) return undefined;
    lockPageScroll();
    return () => unlockPageScroll();
  }, [open]);

  useEffect(() => {
    if (!open || !onClose || suppressEscapeKey) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, suppressEscapeKey]);

  if (!open || typeof document === 'undefined') return null;

  const isBottomSheet = align === 'bottom-sheet';

  const alignItemsClass = isBottomSheet
    ? ''
    : align === 'start'
      ? 'items-start pt-[max(1rem,env(safe-area-inset-top))]'
      : 'items-center';

  const paddingClass = isBottomSheet ? '' : 'p-4 sm:p-6';

  const handleBackdropClick = () => {
    if (closeOnBackdrop && onClose) onClose();
  };

  const dialogPanelClass = isBottomSheet
    ? 'relative w-full max-w-full flex flex-col pointer-events-auto shrink-0 pb-[var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))]'
    : 'relative mx-auto flex w-auto max-w-[100%] flex-col pointer-events-auto shrink-0';

  return createPortal(
    <div
      className={`fixed inset-0 ${zIndexClass}`}
      role="presentation"
      data-acctoo-modal-root="true"
    >
      {/* Backdrop — must stay behind the dialog; only the panel blocks clicks */}
      <div
        className="absolute inset-0 min-h-[100dvh] bg-slate-900/55 backdrop-blur-[3px]"
        aria-hidden={!closeOnBackdrop}
        onClick={closeOnBackdrop ? handleBackdropClick : undefined}
      />

      {isBottomSheet ? (
        <div
          className={`absolute inset-x-0 bottom-0 flex flex-col justify-end pointer-events-none ${className}`}
        >
          <div className={dialogPanelClass} role="dialog" aria-modal="true">
            {children}
          </div>
        </div>
      ) : (
        <div
          className={`absolute inset-0 overflow-y-auto overscroll-contain pointer-events-none ${className}`}
        >
          <div
            className={`flex min-h-full w-full justify-center pointer-events-none ${paddingClass} ${alignItemsClass}`}
          >
            <div className={dialogPanelClass} role="dialog" aria-modal="true">
              {children}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
